import { ChangeEvent, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { confirmBdcImport, previewBdcImport } from "../../../services";
import { BdcImportConfirmData, BdcImportPreviewData } from "../../../types/bdc.type";
import { useBdcBranches } from "../Branch/useBdcBranches";
import {
  BDC_IMPORT_MAX_FILE_BYTES,
  BDC_IMPORT_MAX_FILES,
  bdcQueueLink,
  bdcSettingsLink,
} from "../constants";
import { pcs, thaiDate } from "../format";
import { CARD_SX, TABLE_SX } from "../styles";
import Tag from "../Tag";
import {
  FILE_KIND_LABEL,
  fileKindOf,
  isTxtFile,
  mergeFiles,
} from "./importFiles";
import { BdcConfirmFailure, resolveBdcConfirmFailure, resolveBdcPreviewFailure } from "./importError";
import PreviewSection from "./PreviewSection";

const STEP_LABELS = ["เลือกไฟล์", "ตรวจ + ตัวอย่าง", "จบ"];
const EMPTY_RESPONSE = "ระบบไม่ได้ส่งผลตรวจกลับมา กรุณาลองตรวจไฟล์อีกครั้ง";

/**
 * BDC · นำเข้าไฟล์ EDI
 *
 *   1. เลือกไฟล์          ORDS_*.txt + PICKLST_*.txt หลายไฟล์ (ทั้งวันในครั้งเดียว)
 *   2. ตรวจ + ตัวอย่าง    สรุป, ความครบ DC × รอบ, ผลตรวจ — ยังไม่เขียนฐานข้อมูล (พักไว้ 30 นาที)
 *   3. จบ                 บันทึกแล้ว → ไปคิวงานของวันที่สั่งนั้น
 *
 * PO ที่เคยนำเข้าแล้วถูกเขียนทับทั้งใบ (P1 ยังไม่มีการล็อก) — preview บอกไว้ก่อนยืนยัน
 */
export default function BdcImport() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<BdcImportPreviewData | null>(null);
  const [result, setResult] = useState<BdcImportConfirmData | null>(null);
  const [resultDates, setResultDates] = useState<string[]>([]);
  const [confirmFailure, setConfirmFailure] = useState<BdcConfirmFailure | null>(null);
  const { branches } = useBdcBranches();

  const activeStep = result ? 2 : preview ? 1 : 0;

  const handlePick = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (picked.length === 0) return;
    const notTxt = picked.filter((file) => !isTxtFile(file));
    const tooBig = picked.filter((file) => isTxtFile(file) && file.size > BDC_IMPORT_MAX_FILE_BYTES);
    const accepted = picked.filter((file) => isTxtFile(file) && file.size <= BDC_IMPORT_MAX_FILE_BYTES);
    const merged = mergeFiles(files, accepted);
    const problems = [
      notTxt.length > 0 && `ข้ามไฟล์ที่ไม่ใช่ .txt: ${notTxt.map((file) => file.name).join(", ")}`,
      tooBig.length > 0 && `ข้ามไฟล์ที่ใหญ่เกิน 10MB: ${tooBig.map((file) => file.name).join(", ")}`,
      merged.length > BDC_IMPORT_MAX_FILES &&
        `เลือกได้ไม่เกิน ${BDC_IMPORT_MAX_FILES} ไฟล์ต่อครั้ง — เอาไฟล์ออกให้เหลือไม่เกินจำนวนนี้`,
    ].filter(Boolean);
    setFailure(problems.length > 0 ? problems.join(" · ") : null);
    setPreview(null);
    setConfirmFailure(null);
    setFiles(merged);
  };

  const handleRemove = (name: string) => {
    setPreview(null);
    setConfirmFailure(null);
    setFailure(null);
    setFiles((current) => current.filter((file) => file.name !== name));
  };

  const handleCheck = async () => {
    if (files.length === 0) return;
    setChecking(true);
    setFailure(null);
    setConfirmFailure(null);
    try {
      const {
        data: { data },
      } = await previewBdcImport(files);
      if (!data) {
        setPreview(null);
        setFailure(EMPTY_RESPONSE);
        return;
      }
      setPreview(data);
    } catch (error) {
      setPreview(null);
      setFailure(resolveBdcPreviewFailure(error));
    } finally {
      setChecking(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    setApplying(true);
    setConfirmFailure(null);
    try {
      const {
        data: { data },
      } = await confirmBdcImport(preview.upload_id);
      if (!data) {
        setPreview(null);
        setFailure(EMPTY_RESPONSE);
        return;
      }
      setResultDates(preview.order_dates);
      setResult(data);
      setPreview(null);
      setFiles([]);
    } catch (error) {
      const reason = resolveBdcConfirmFailure(error);
      if (reason.kind === "expired") {
        // ผลตรวจหมดอายุ — ตรวจใหม่ได้จากการ์ดเลือกไฟล์ (ไฟล์ยังอยู่ในรายการ)
        setPreview(null);
        setFailure(reason.message);
      } else {
        setConfirmFailure(reason);
      }
    } finally {
      setApplying(false);
    }
  };

  const handleRestart = () => {
    setResult(null);
    setResultDates([]);
    setFiles([]);
    setFailure(null);
  };

  const ordsCount = files.filter((file) => fileKindOf(file.name) === "ORDS").length;
  const pickCount = files.filter((file) => fileKindOf(file.name) === "PICKLST").length;

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box mb={2}>
        <Typography variant="h6" fontWeight={700}>
          BDC · นำเข้าไฟล์ EDI
        </Typography>
        <Typography variant="body2" color="text.secondary">
          นำเข้าไฟล์ ORDS (PO) และ PICKLST (รายร้าน) ของ 7-Eleven DC แล้วระบบสร้างงานส่งต่อ DC × รอบ
        </Typography>
      </Box>

      <Stack gap={2}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEP_LABELS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {!result && (
          <Card sx={{ ...CARD_SX, p: 2 }}>
            <Stack gap={2} sx={TABLE_SX}>
              <Box
                sx={{
                  border: "2px dashed #C7CAD2",
                  borderRadius: "12px",
                  p: 2.5,
                  textAlign: "center",
                  bgcolor: "#FAFBFC",
                }}
              >
                <Typography variant="body2" color="text.secondary" mb={1}>
                  เลือกไฟล์ ORDS_*.txt และ PICKLST_*.txt ของวันเดียวกันให้ครบ (เลือกได้หลายไฟล์ เพิ่มทีละชุดได้)
                </Typography>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept=".txt,text/plain"
                  onChange={handlePick}
                  style={{ display: "none" }}
                />
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => inputRef.current?.click()}
                  disabled={checking || applying}
                >
                  เลือกไฟล์
                </Button>
              </Box>

              {files.length > 0 && (
                <Box sx={{ maxHeight: 280, overflow: "auto" }}>
                  <table className="bdc-t">
                    <thead>
                      <tr>
                        <th>ไฟล์</th>
                        <th>ชนิด</th>
                        <th className="n">ขนาด</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {files.map((file) => {
                        const kind = fileKindOf(file.name);
                        return (
                          <tr key={file.name}>
                            <td className="mono">{file.name}</td>
                            <td>
                              {kind === "UNKNOWN" ? (
                                <Tag tone="warn">{FILE_KIND_LABEL[kind]}</Tag>
                              ) : (
                                FILE_KIND_LABEL[kind]
                              )}
                            </td>
                            <td className="n">{pcs(Math.ceil(file.size / 1024))} KB</td>
                            <td className="c">
                              <IconButton
                                size="small"
                                aria-label={`เอา ${file.name} ออก`}
                                onClick={() => handleRemove(file.name)}
                                disabled={checking || applying}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </Box>
              )}

              <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                <Button
                  variant="contained"
                  onClick={handleCheck}
                  loading={checking}
                  disabled={files.length === 0 || files.length > BDC_IMPORT_MAX_FILES || applying}
                >
                  ตรวจไฟล์
                </Button>
                {files.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {files.length} ไฟล์ · ORDS {ordsCount} · PICKLST {pickCount}
                  </Typography>
                )}
                {files.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => {
                      setFiles([]);
                      setPreview(null);
                      setConfirmFailure(null);
                    }}
                    disabled={checking || applying}
                  >
                    ล้างรายการไฟล์
                  </Button>
                )}
              </Stack>

              {failure && (
                <Alert severity="error">
                  <AlertTitle sx={{ mb: 0 }}>ไฟล์ชุดนี้ยังใช้ไม่ได้</AlertTitle>
                  {failure}
                </Alert>
              )}
            </Stack>
          </Card>
        )}

        {preview && (
          <PreviewSection
            key={preview.upload_id}
            preview={preview}
            applying={applying}
            onConfirm={handleConfirm}
            branches={branches}
            dcSettingsLink={
              <Button size="small" variant="outlined" color="inherit" component={RouterLink} to={bdcSettingsLink("dc")}>
                ไปที่ ตั้งค่า BDC › DC
              </Button>
            }
            confirmNotice={
              confirmFailure && (
                <Alert
                  severity="error"
                  action={
                    confirmFailure.kind === "bind_dc" ? (
                      <Button color="inherit" size="small" component={RouterLink} to={bdcSettingsLink("dc")}>
                        ผูก DC
                      </Button>
                    ) : undefined
                  }
                >
                  <AlertTitle sx={{ mb: 0 }}>
                    {confirmFailure.kind === "retry" ? "ยังไม่ได้บันทึก — กดยืนยันอีกครั้งได้" : "ยืนยันนำเข้าไม่สำเร็จ"}
                  </AlertTitle>
                  {confirmFailure.message}
                </Alert>
              )
            }
          />
        )}

        {result && (
          <Card sx={{ ...CARD_SX, p: 2 }}>
            <Stack gap={2}>
              <Alert severity="success">
                <AlertTitle>นำเข้าเรียบร้อย</AlertTitle>
                บันทึก {pcs(result.saved_shipments)} shipment
                {result.overwritten > 0 && ` (เขียนทับของเดิม ${pcs(result.overwritten)} PO)`}
                {` · ร้านใหม่ ${pcs(result.new_stores)} ร้าน`}
              </Alert>
              {(result.skipped_locked?.length ?? 0) > 0 && (
                <Alert severity="info" data-testid="bdc-skipped-locked">
                  ไม่เขียนทับ {pcs(result.skipped_locked?.length ?? 0)} PO ที่ยืนยัน DO แล้ว: {result.skipped_locked?.join(", ")}
                </Alert>
              )}
              <Stack direction="row" gap={1.5} flexWrap="wrap">
                {resultDates.map((date) => (
                  <Button key={date} variant="contained" component={RouterLink} to={bdcQueueLink(date)}>
                    ไปคิวงาน วันที่สั่ง {thaiDate(date)}
                  </Button>
                ))}
                <Button variant="outlined" onClick={handleRestart}>
                  นำเข้าไฟล์ชุดใหม่
                </Button>
              </Stack>
            </Stack>
          </Card>
        )}
      </Stack>
    </Box>
  );
}
